import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, RotateCcw, Check, X, ImagePlus, LoaderCircle, ZoomIn, ZoomOut, Upload } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { cropToPassportJpeg, decodeImage, PASSPORT_W, PASSPORT_H } from "@/lib/photo";

type Step = "camera" | "crop" | "confirm";

/** Crop window geometry over the source bitmap (image px). */
interface CropWindow {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

interface PatientPhotoCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the finalized 413×531 300-DPI JPEG blob after user confirms. */
  onConfirm: (photo: Blob) => void | Promise<void>;
  /** Whether an existing photo will be replaced (affects button labels only). */
  replacing?: boolean;
}

/**
 * Passport-photo capture window: camera dropdown → live preview with a face
 * guide → capture → crop (fixed 35×45 aspect, drag + zoom) → confirm.
 * A file picker is offered as fallback (desktops without webcams / scanning
 * existing photos). The same dialog is used from the add-patient form, the
 * patient detail page — wherever a photo is needed.
 *
 * Camera lifecycle rules:
 * - getUserMedia starts ONLY while the dialog is open and on the camera step
 *   (privacy: the light goes off as soon as you leave the step or close).
 * - The stream is stopped on unmount, on close, and on step transitions.
 * - deviceId changes (camera dropdown) re-acquire the stream with the new
 *   device; the old stream is released first.
 */
export function PatientPhotoCaptureDialog({
  open, onOpenChange, onConfirm, replacing,
}: PatientPhotoCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [step, setStep] = useState<Step>("camera");
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>("");
  const [camError, setCamError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [facing, setFacing] = useState<"user" | "environment">("user");

  // Raw captured/loaded frame awaiting crop.
  const [frame, setFrame] = useState<{ bitmap: ImageBitmap; mirrored: boolean } | null>(null);
  const [crop, setCrop] = useState({ cx: 0.5, cy: 0.42, zoom: 1 });
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Enumerate cameras. Labels/IDs are only populated once camera permission
  // is granted, so this re-runs after the stream starts (see startStream).
  // Entries without a deviceId (pre-permission placeholders) are dropped —
  // Radix Select items require a non-empty value.
  const listDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === "videoinput" && d.deviceId));
    } catch { /* older browsers — dropdown stays empty; default camera used */ }
  }, []);

  const startStream = useCallback(async (id?: string) => {
    stopStream();
    setStarting(true);
    setCamError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: id
          ? { deviceId: { exact: id } }
          : { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      // Permission was just granted — real labels/IDs are available now. The
      // on-open enumeration ran before the prompt, with empty deviceIds.
      listDevices();
    } catch (e) {
      setCamError(
        (e as Error).name === "NotAllowedError"
          ? "Camera permission was denied. Allow camera access or use the file option."
          : (e as Error).name === "NotFoundError"
            ? "No camera found on this device. Use the file option to pick an image."
            : `Camera error: ${(e as Error).message}`,
      );
    } finally {
      setStarting(false);
    }
  }, [facing, stopStream, listDevices]);

  // Open → camera step; acquire. Close/step-change → release.
  // `facing` is a dependency: the front/back switch re-acquires the stream
  // (facingMode only applies at acquisition time).
  useEffect(() => {
    if (open && step === "camera") {
      startStream(deviceId || undefined);
    } else {
      stopStream();
    }
    return stopStream;
  }, [open, step, deviceId, facing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) listDevices();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when the dialog closes fully.
  useEffect(() => {
    if (!open) {
      setStep("camera");
      setFrame(null);
      setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
      setCrop({ cx: 0.5, cy: 0.42, zoom: 1 });
      setCamError(null);
    }
  }, [open]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d")!;
    if (facing === "user") {
      // Mirror-preview ⇒ mirror the capture so it matches what the user saw.
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(async (b) => {
      if (!b) { toast.error("Capture failed"); return; }
      const bitmap = await decodeImage(b);
      setFrame({ bitmap, mirrored: facing === "user" });
      setCrop({ cx: 0.5, cy: 0.42, zoom: 1 });
      setStep("crop");
    }, "image/jpeg", 0.95);
  }, [facing]);

  const onFileChosen = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    try {
      const bitmap = await decodeImage(file);
      setFrame({ bitmap, mirrored: false });
      setCrop({ cx: 0.5, cy: 0.42, zoom: 1 });
      setStep("crop");
    } catch {
      toast.error("Could not read that image — try a different file");
    }
  }, []);

  const reset = useCallback(() => {
    setStep("camera");
    setFrame(null);
  }, []);

  /**
   * Crop geometry: fixed 35×45 (413×531) window over the source bitmap.
   * zoom=1 → window is the largest 413:531 rect that fits the image; higher
   * zoom shrinks the window (closer view). cx/cy are window-center fractions
   * clamped so the window stays fully inside the image.
   */
  const cropWindow = useCallback((): CropWindow | null => {
    if (!frame) return null;
    const { bitmap } = frame;
    const baseW = Math.min(bitmap.width, (bitmap.height * PASSPORT_W) / PASSPORT_H);
    const baseH = baseW * (PASSPORT_H / PASSPORT_W);
    const w = baseW / crop.zoom;
    const h = baseH / crop.zoom;
    const cx = Math.min(Math.max(crop.cx * bitmap.width, w / 2), bitmap.width - w / 2);
    const cy = Math.min(Math.max(crop.cy * bitmap.height, h / 2), bitmap.height - h / 2);
    return { sx: cx - w / 2, sy: cy - h / 2, sw: w, sh: h };
  }, [frame, crop]);

  const confirmCrop = useCallback(async () => {
    if (!frame) return;
    const win = cropWindow();
    if (!win) return;
    setSaving(true);
    try {
      const blob = await cropToPassportJpeg(frame.bitmap, win, frame.mirrored);
      const url = URL.createObjectURL(blob);
      setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return url; });
      setStep("confirm");
    } catch (e) {
      toast.error(`Photo processing failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }, [frame, cropWindow]);

  const confirmSave = useCallback(async () => {
    if (!previewUrl) return;
    setSaving(true);
    try {
      const blob = await (await fetch(previewUrl)).blob();
      await onConfirm(blob);
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [previewUrl, onConfirm, onOpenChange]);

  // Drag-to-position the crop window.
  const draggingRef = useRef<{ x: number; y: number } | null>(null);
  const onDragStart = useCallback((e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = { x: e.clientX, y: e.clientY };
  }, []);
  const onDragMove = useCallback((e: React.PointerEvent) => {
    const start = draggingRef.current;
    if (!start || !frame) return;
    const win = cropWindow();
    if (!win) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // The preview rect shows the crop WINDOW (win.sw bitmap px across
    // rect.width CSS px), not the whole bitmap — so 1 CSS px of drag =
    // win.sw / rect.width image px. Same factor vertically via win.sh.
    const scale = win.sw / rect.width;
    setCrop((c) => ({
      ...c,
      cx: c.cx - ((e.clientX - start.x) * scale) / frame.bitmap.width,
      cy: c.cy - ((e.clientY - start.y) * scale) / frame.bitmap.height,
    }));
    draggingRef.current = { x: e.clientX, y: e.clientY };
  }, [frame, cropWindow]);
  const onDragEnd = useCallback(() => { draggingRef.current = null; }, []);

  // Keyboard zoom for accessibility.
  useEffect(() => {
    if (step !== "crop") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "+" || e.key === "=") setCrop((c) => ({ ...c, zoom: Math.min(4, c.zoom * 1.15) }));
      if (e.key === "-") setCrop((c) => ({ ...c, zoom: Math.max(1, c.zoom / 1.15) }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  return (
    <Dialog open={open} onOpenChange={(o) => { stopStream(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{replacing ? "Replace patient photo" : "Add patient photo"}</DialogTitle>
          <DialogDescription>
            Passport size 35 × 45 mm (413 × 531 px, 300 DPI). {step === "camera" && "Position the face inside the guide and capture."}
            {step === "crop" && "Drag to position, +/- to zoom (or use the buttons)."}
            {step === "confirm" && "Check the photo below and confirm."}
          </DialogDescription>
        </DialogHeader>

        {step === "camera" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Select value={deviceId} onValueChange={setDeviceId}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Select camera…" /></SelectTrigger>
                <SelectContent>
                  {devices.map((d, i) => (
                    <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button" variant="outline" size="icon"
                title="Switch front/back camera"
                onClick={() => { setDeviceId(""); setFacing((f) => (f === "user" ? "environment" : "user")); }}
              >
                <RotateCcw size={16} />
              </Button>
            </div>

            <div className="relative aspect-[413/531] w-full overflow-hidden rounded-md bg-dark-900">
              <video ref={videoRef} playsInline muted autoPlay
                className={cn("h-full w-full object-cover", facing === "user" && "scale-x-[-1]")} />
              {/* Passport face guide — head 70–80% of height, per ICAO-style guidance. */}
              <svg viewBox="0 0 413 531" preserveAspectRatio="xMidYMid slice" className="pointer-events-none absolute inset-0 h-full w-full">
                <ellipse cx="206.5" cy="215" rx="95" ry="120" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeDasharray="6 4" />
                <line x1="120" y1="430" x2="293" y2="430" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeDasharray="6 4" />
              </svg>
              {starting && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-cream-50">
                  <LoaderCircle size={24} className="animate-spin" />
                </div>
              )}
              {camError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 p-6 text-center text-cream-50">
                  <Camera size={32} />
                  <p className="text-sm">{camError}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload size={16} className="mr-1" /> Choose file…
              </Button>
              <Button
                type="button"
                disabled={!!camError || starting}
                onClick={capture}
                className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
              >
                <Camera size={16} className="mr-1" /> Capture
              </Button>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFileChosen(f); e.target.value = ""; }} />
          </div>
        )}

        {step === "crop" && frame && (
          <div className="space-y-3">
            <div
              className="relative aspect-[413/531] w-full cursor-grab overflow-hidden rounded-md bg-dark-900 active:cursor-grabbing touch-none select-none"
              onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd}
            >
              <CropCanvas bitmap={frame.bitmap} win={cropWindow()} mirrored={frame.mirrored} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="icon" title="Zoom out"
                  onClick={() => setCrop((c) => ({ ...c, zoom: Math.max(1, c.zoom / 1.15) }))}>
                  <ZoomOut size={16} />
                </Button>
                <Button type="button" variant="outline" size="icon" title="Zoom in"
                  onClick={() => setCrop((c) => ({ ...c, zoom: Math.min(4, c.zoom * 1.15) }))}>
                  <ZoomIn size={16} />
                </Button>
              </div>
              <Button type="button" variant="ghost" onClick={reset}><X size={16} className="mr-1" /> Retake</Button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-3">
            {previewUrl && (
              <div className="flex justify-center">
                <img src={previewUrl} alt="Captured photo preview"
                  className="h-auto w-auto max-h-[60vh] rounded-md border border-cream-200 object-contain dark:border-dark-700" />
              </div>
            )}
            <p className="text-center text-xs text-muted">413 × 531 px · 300 DPI · JPEG</p>
          </div>
        )}

        <DialogFooter>
          {step === "camera" && <Button type="button" variant="ghost" onClick={() => { stopStream(); onOpenChange(false); }}>Cancel</Button>}
          {step === "crop" && (
            <>
              <Button type="button" variant="ghost" onClick={reset}>Back</Button>
              <Button type="button" onClick={confirmCrop} disabled={saving} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                {saving ? <LoaderCircle size={16} className="mr-1 animate-spin" /> : <ImagePlus size={16} className="mr-1" />}
                Continue
              </Button>
            </>
          )}
          {step === "confirm" && (
            <>
              <Button type="button" variant="ghost" onClick={reset}>Retake</Button>
              <Button type="button" onClick={confirmSave} disabled={saving} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                {saving ? <LoaderCircle size={16} className="mr-1 animate-spin" /> : <Check size={16} className="mr-1" />}
                {replacing ? "Confirm & replace" : "Confirm & save"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Live crop preview — draws the source bitmap zoomed/panned under a fixed
 * 35×45 viewport with a dimmed outside-window mask.
 */
function CropCanvas({ bitmap, win, mirrored }: { bitmap: ImageBitmap; win: CropWindow | null; mirrored: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !win) return;
    // Render at passport resolution for a faithful preview of the final JPEG.
    canvas.width = PASSPORT_W;
    canvas.height = PASSPORT_H;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#1c1917";
    ctx.fillRect(0, 0, PASSPORT_W, PASSPORT_H);
    if (mirrored) { ctx.translate(PASSPORT_W, 0); ctx.scale(-1, 1); }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, win.sx, win.sy, win.sw, win.sh, 0, 0, PASSPORT_W, PASSPORT_H);
    if (mirrored) { ctx.setTransform(1, 0, 0, 1, 0, 0); }
  }, [bitmap, win, mirrored]);

  return <canvas ref={canvasRef} className="h-full w-full object-contain" />;
}
